// The store app the web app is running inside (the "containing app"), read from the user agent.
//
// Since version 24.0 both store apps append one token to the stock WebView user agent (see "User agent
// contract" in the android and ios-webapp repos' CLAUDE.md):
//
//   SingYourPart/<app version> (<OS> <OS version>)
//
//   e.g. "... Mobile Safari/537.36 SingYourPart/24.0 (Android 14)"
//        "... Mobile/15E148 SingYourPart/24.0 (iOS 18.5)"          (iPadOS on an iPad)
//
// The app version is one to three dot-separated integers. Its major number is a release counter shared
// by the two apps (the same major means they shipped together); minor and patch are small platform-local
// fixes. The OS version travels in the token because the stock user agent no longer carries it: Chrome
// freezes its Android token at "Android 10", iOS 26 reports "OS 18_7", and an iPad WKWebView claims to
// be a Mac.
//
// Store apps older than 24.0 have no token. Until 2027-10-13 the web app still recognizes them through
// deprecated fallbacks (a bare "Sing Your Part" user agent for Android 23 and earlier, window.webkit for
// iOS 4.x; see billing.ts), but it cannot learn an Android version from them and learns the iOS version
// only through the deprecated getAppVersion reply.

export enum ContainingAppPlatform {
  Android = 'android',
  iOS = 'ios',
}

export interface ContainingApp {
  platform: ContainingAppPlatform;
  appVersion: string; // e.g. '24.0'
  os: 'Android' | 'iOS' | 'iPadOS';
  osVersion: string; // e.g. '14' or '18.5'
}

const containingAppToken = /\bSingYourPart\/(\d+(?:\.\d+){0,2}) \((Android|iOS|iPadOS) (\d+(?:\.\d+)*)\)/;

export function parseContainingApp(userAgent: string | undefined): ContainingApp | undefined {
  const match = userAgent?.match(containingAppToken);
  if (!match) {
    return undefined;
  }
  const [, appVersion, os, osVersion] = match;
  return {
    platform: os === 'Android' ? ContainingAppPlatform.Android : ContainingAppPlatform.iOS,
    appVersion,
    os: os as ContainingApp['os'],
    osVersion,
  };
}

// The containing app for this page, or undefined in a browser or in a store app older than 24.0
export function getContainingApp(): ContainingApp | undefined {
  return parseContainingApp(typeof navigator === 'undefined' ? undefined : navigator.userAgent);
}

// Orders dot-separated versions numerically by segment ('4.9' < '4.10', '24.0.18' < '24.1'); missing
// segments count as zero ('24' equals '24.0')
export function compareAppVersions(left: string, right: string): number {
  const leftParts = left.split('.').map(Number);
  const rightParts = right.split('.').map(Number);
  const length = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < length; i++) {
    const difference = (leftParts[i] || 0) - (rightParts[i] || 0);
    if (difference !== 0) {
      return difference;
    }
  }
  return 0;
}

// True inside a store app whose version is `version` or newer; false in a browser or in an older app
export function isContainingAppAtLeast(version: string): boolean {
  const app = getContainingApp();
  return !!app && compareAppVersions(app.appVersion, version) >= 0;
}

export function containingAppPlatformLabel(platform: ContainingAppPlatform): 'Android' | 'iOS' {
  return platform === ContainingAppPlatform.Android ? 'Android' : 'iOS';
}

/**
 * @deprecated remove after 2027-10-13: Google Play app versions 23 and earlier set the bare user agent
 * "Sing Your Part" instead of the token; by then they will have updated to 24.0 or be unsupported.
 * Delete with isInsideGooglePlayApp's fallback in billing.ts and the Learn update screen.
 */
export function isLegacyGooglePlayAppUserAgent(userAgent: string): boolean {
  return userAgent.trim().toLowerCase() === 'sing your part';
}
